/**
 * Focus trap composable for modals and dialogs
 * Ensures keyboard focus stays within a container element
 * WCAG 2.4.3 Focus Order, 2.1.2 No Keyboard Trap compliance
 */

const FOCUSABLE_SELECTORS = [
    'a[href]:not([disabled]):not([tabindex="-1"])',
    'button:not([disabled]):not([tabindex="-1"])',
    'input:not([disabled]):not([tabindex="-1"])',
    'select:not([disabled]):not([tabindex="-1"])',
    'textarea:not([disabled]):not([tabindex="-1"])',
    '[tabindex]:not([tabindex="-1"]):not([disabled])',
].join(', ')

export interface FocusTrapOptions {
    /** Element to focus when trap is activated (defaults to first focusable) */
    initialFocus?: Ref<HTMLElement | undefined>
    /** Whether to return focus to trigger element on deactivation */
    returnFocusOnDeactivate?: boolean
    /** Callback when user tries to escape (e.g., for close action) */
    onEscape?: () => void
    /** Whether to actively monitor and trap focus escapes (for dynamic content) */
    watchFocus?: boolean
}

export function useFocusTrap(
    containerRef: Ref<HTMLElement | undefined>,
    options: FocusTrapOptions = {}
) {
    const {
        initialFocus,
        returnFocusOnDeactivate = true,
        onEscape,
        watchFocus = true,
    } = options

    const isActive = ref(false)
    const triggerElement = ref<HTMLElement | null>(null)
    let focusWatcher: (() => void) | null = null

    /**
     * Get all focusable elements within the container
     */
    function getFocusableElements(): HTMLElement[] {
        if (!containerRef.value) return []
        const elements = containerRef.value.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS)
        return Array.from(elements).filter((el) => {
            // Filter out elements that are not visible
            return el.offsetParent !== null || el.offsetWidth > 0 || el.offsetHeight > 0
        })
    }

    /**
     * Handle Tab key to trap focus within container
     */
    function handleKeyDown(event: KeyboardEvent): void {
        if (!isActive.value || !containerRef.value) return

        if (event.key === 'Escape' && onEscape) {
            event.preventDefault()
            onEscape()
            return
        }

        if (event.key !== 'Tab') return

        const focusableElements = getFocusableElements()
        if (focusableElements.length === 0) return

        const firstElement = focusableElements[0]
        const lastElement = focusableElements[focusableElements.length - 1]
        const activeElement = document.activeElement as HTMLElement

        // Shift+Tab on first element -> go to last
        if (event.shiftKey && activeElement === firstElement) {
            event.preventDefault()
            lastElement?.focus()
            return
        }

        // Tab on last element -> go to first
        if (!event.shiftKey && activeElement === lastElement) {
            event.preventDefault()
            firstElement?.focus()
            return
        }

        // If focus is outside container, bring it back
        if (!containerRef.value.contains(activeElement)) {
            event.preventDefault()
            if (event.shiftKey) {
                lastElement?.focus()
            } else {
                firstElement?.focus()
            }
        }
    }

    /**
     * Handle focus events to catch focus escapes (for dynamic content like DearFlip)
     */
    function handleFocusIn(event: FocusEvent): void {
        if (!isActive.value || !containerRef.value) return

        const target = event.target as HTMLElement
        const modalWrap = document.getElementById('modal-wrap')

        // Check if focus went outside both the modal container and modal-wrap
        // This allows focus within the modal teleport destination
        if (!containerRef.value.contains(target) && !modalWrap?.contains(target)) {
            // Focus escaped! Bring it back to the container
            event.preventDefault()
            event.stopPropagation()

            const focusableElements = getFocusableElements()
            if (focusableElements.length > 0) {
                focusableElements[0]?.focus()
            } else if (containerRef.value) {
                containerRef.value.focus()
            }
        }
    }

    /**
     * Start watching for focus escapes
     */
    function startFocusWatch(): void {
        if (focusWatcher) return
        document.addEventListener('focusin', handleFocusIn, true)
        focusWatcher = () => {
            document.removeEventListener('focusin', handleFocusIn, true)
        }
    }

    /**
     * Stop watching for focus escapes
     */
    function stopFocusWatch(): void {
        if (focusWatcher) {
            focusWatcher()
            focusWatcher = null
        }
    }

    /**
     * Activate the focus trap
     */
    function activate(): void {
        if (isActive.value) return

        // Store the element that triggered the modal
        triggerElement.value = document.activeElement as HTMLElement
        isActive.value = true

        // Add keydown listener
        document.addEventListener('keydown', handleKeyDown)

        // Start watching for focus escapes (handles dynamic content)
        if (watchFocus) {
            startFocusWatch()
        }

        // Set initial focus, then set inert on siblings
        // IMPORTANT: Focus must be moved BEFORE setting inert to avoid aria-hidden warning
        nextTick(() => {
            // Check if trap was deactivated before nextTick ran (race condition)
            if (!isActive.value) return

            if (initialFocus?.value) {
                initialFocus.value.focus()
            } else {
                const focusableElements = getFocusableElements()
                if (focusableElements.length > 0) {
                    focusableElements[0]?.focus()
                } else if (containerRef.value) {
                    // If no focusable elements, focus the container itself
                    containerRef.value.setAttribute('tabindex', '-1')
                    containerRef.value.focus()
                }
            }

            // Set inert on siblings AFTER focus is moved to modal
            // This prevents "aria-hidden on element with focused descendant" warning
            setInertOnSiblings(true)
        })
    }

    /**
     * Deactivate the focus trap
     */
    function deactivate(): void {
        if (!isActive.value) return

        isActive.value = false
        document.removeEventListener('keydown', handleKeyDown)

        // Stop watching for focus escapes
        stopFocusWatch()

        // Remove inert from siblings
        setInertOnSiblings(false)

        // Return focus to trigger element
        if (returnFocusOnDeactivate && triggerElement.value) {
            nextTick(() => {
                triggerElement.value?.focus()
            })
        }
    }

    /**
     * Set or remove aria-hidden on background content
     * This hides background content from screen readers when modal is open
     * NOTE: We don't use 'inert' because #modal-wrap is inside #__nuxt,
     * so setting inert on #__nuxt would also make the modal inert
     */
    function setInertOnSiblings(hide: boolean): void {
        const nuxtApp = document.getElementById('__nuxt')
        if (!nuxtApp) return

        // Find main content areas to hide from screen readers (but NOT modal-wrap)
        const mainContent = nuxtApp.querySelector('main')
        const header = nuxtApp.querySelector('header')
        const footer = nuxtApp.querySelector('footer')

        const elementsToHide = [mainContent, header, footer].filter(Boolean) as HTMLElement[]

        elementsToHide.forEach((el) => {
            if (hide) {
                el.setAttribute('aria-hidden', 'true')
            } else {
                el.removeAttribute('aria-hidden')
            }
        })
    }

    // Cleanup on unmount
    onUnmounted(() => {
        if (isActive.value) {
            deactivate()
        }
    })

    return {
        isActive: readonly(isActive),
        activate,
        deactivate,
        getFocusableElements,
    }
}
